import { Component } from '@theme/component';

class ProductTabsComponent extends Component {
  requiredRefs = ['tabButtons', 'accordionHeaders', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => this.#select(index));
      button.addEventListener('keydown', (event) => this.#onTabKeydown(event, index));
    });

    // Below the 750px breakpoint the desktop tab bar is hidden and these
    // per-panel headers render as accordion toggles instead. They drive the
    // same shared state as the desktop tabs (see #select).
    this.refs.accordionHeaders.forEach((button, index) => {
      button.addEventListener('click', () => this.#select(index));
      button.addEventListener('keydown', (event) => this.#onActivateKey(event, index));
    });

    this.querySelectorAll('[data-scroll-to-reviews]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = this.ownerDocument.querySelector('[id*="judge_me_reviews_review_widget"]');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
      });
    });
  }

  /**
   * Enter/Space activate the focused tab or accordion header, matching
   * native button semantics (needed here because #select must also run
   * for the accordion headers, which aren't a role="tab" roving group).
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onActivateKey(event, index) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#select(index);
  }

  /**
   * WAI-ARIA APG Tabs pattern: Left/Right roves focus between tabs (wrapping),
   * Home/End jump to the first/last tab, Enter/Space activates.
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onTabKeydown(event, index) {
    const tabs = this.refs.tabButtons;
    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        this.#onActivateKey(event, index);
        return;
    }
    event.preventDefault();
    this.#select(nextIndex);
    tabs[nextIndex].focus();
  }

  /** @param {number} index */
  #select(index) {
    this.refs.tabButtons.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('product-tabs__tab--active', isActive);
      button.setAttribute('aria-selected', String(isActive));
      button.tabIndex = isActive ? 0 : -1;
    });
    this.refs.accordionHeaders.forEach((button, i) => {
      const isActive = i === index;
      button.classList.toggle('product-tabs__accordion-header--active', isActive);
      button.setAttribute('aria-expanded', String(isActive));
    });
    this.refs.panels.forEach((panel, i) => {
      panel.classList.toggle('product-tabs__panel--active', i === index);
    });
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabsComponent);
}
